import core, { App, GQL, View } from '@gin-betting/core';
import { createStructuredSelector } from 'reselect';

import { useMemoSelector } from 'hooks';
import { selectOverviewScores } from 'selectors';
import type { MatchPartInfo } from 'types';

const {
    entities: {
        selectors: { makeMatchSelector },
    },
} = core;

interface Input {
    sportEventId: string;
}

interface Output {
    meta: View.Meta[];
    scores: View.ScoreInfo[];
    winner: GQL.CompetitorHomeAway;
    currentMatchPart: MatchPartInfo | null;
    sportId: string | undefined;
}

const DEFAULT_TOTAL: View.ScoreInfo = {
    home: '',
    away: '',
    type: App.ScoreTypes.TYPE_TOTAL,
    indexNumber: 0,
};

const MAX_COLUMN = 4 as const;

const useScore = ({ sportEventId }: Input): Output => {
    const {
        sportEvent,
        scores: { scores, currentMatchPart, meta },
    } = useMemoSelector(makeSelectProps(), { matchId: sportEventId });

    const isEnded = sportEvent?.status === GQL.SportEventStatus.ENDED;

    const totalScore =
        scores.splice(
            scores.findIndex(({ type }) => type === App.ScoreTypes.TYPE_TOTAL),
            1
        )[0] || DEFAULT_TOTAL;

    const { home, away } = totalScore;

    const winner =
        isEnded && !!home && !!away && +home !== +away
            ? +home > +away
                ? GQL.CompetitorHomeAway.HOME
                : GQL.CompetitorHomeAway.AWAY
            : GQL.CompetitorHomeAway.UNKNOWN;

    return {
        scores: [...scores.slice(0, MAX_COLUMN - 1), totalScore],
        winner,
        currentMatchPart,
        meta,
        sportId: sportEvent?.sportId,
    };
};

export default useScore;

const makeSelectProps = () => {
    return createStructuredSelector({
        scores: selectOverviewScores,
        sportEvent: makeMatchSelector({ propsToPick: ['status', 'sportId'] }),
    });
};
